import GroupLabel from '@/components/UI/GroupLabel/GroupLabel';
import Link from '@/components/UI/Link';
import UserAvatar from '@/components/UI/UserAvatar';
import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { isDead } from '@/constants/utils';
import { web as webRoutes } from '@/support/route';
import { observationFiltersToTitle } from '@/referenceData/study/observations';
import { DateTimeRenderer } from '@/components/UI/DateRenderers/DateRenderers';
import type { TanStackColumnDef, FilterOption } from '@/components/UI/Table/TableComponent.model';
import type { ObservationV1 } from '@/model/Observation.model';
import type { ID } from '@/model/Common.model';
import { _isNotBlank } from '@/littledash';

const unFilteredCols = ['created_by', 'text'];

export interface ObservationWithRelations extends ObservationV1 {
  animal: { id: ID; name: string; status?: string; cage?: { name: string } };
  cage?: { name: string };
  study_group?: any;
  created_by: { name: string };
}

export const columns = (studyId: ID): Array<TanStackColumnDef<ObservationWithRelations>> => [
  {
    id: 'animal',
    header: 'Animal',
    accessorPath: 'animal?.name',
    accessorFn: (row) => row.animal?.name,
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }) => (
      <Link
        className="link"
        to={webRoutes('animals.show', {
          id: studyId,
          subject_id: original.animal?.id,
        })}
      >
        {original.animal?.name}
      </Link>
    ),
  },
  {
    id: 'cage',
    header: 'Cage',
    accessorPath: 'cage?.name',
    accessorFn: (row) => row.cage?.name,
    isVisible: true,
    enableSorting: true,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    cell: ({ row: { original } }) => original.cage?.name || isDead(original.animal as any),
  },
  {
    id: 'study_group',
    header: 'Study Group',
    isVisible: true,
    enableSorting: true,
    accessorPath: 'study_group?.name',
    accessorFn: (row) => row.study_group?.name,
    cell: ({
      row: {
        original: { study_group: group },
      },
    }) => <GroupLabel group={group} />,
  },
  {
    id: 'observation',
    header: 'Observation',
    isVisible: true,
    enableSorting: true,
    accessorKey: 'title',
  },
  {
    id: 'score',
    header: 'Score',
    accessorKey: 'value',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }) => (
      <div
        data-tooltip-id="global-tooltip-id"
        data-tooltip-content={
          // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
          original?.options?.min ? `Scale: ${original.options?.min}-${original.options?.max}` : undefined
        }
      >
        {original?.value}
      </div>
    ),
  },
  {
    id: 'comment',
    header: 'Comment',
    accessorKey: 'text',
    isVisible: true,
    enableSorting: true,
    cell: ({
      row: {
        original: { text },
      },
    }) => (_isNotBlank(text) ? text : '-'),
  },
  {
    id: 'recorded',
    header: 'Recorded',
    accessorKey: 'observed_at',
    isVisible: true,
    enableSorting: true,
    cell: ({ row: { original } }) => <DateTimeRenderer value={original.observed_at} />,
  },
  {
    id: 'recorded_by',
    header: 'Recorded by',
    isVisible: true,
    enableSorting: true,
    accessorKey: 'created_by',
    cell: ({
      row: {
        original: { created_by: createdBy },
      },
    }) => <UserAvatar user={createdBy} className="ml1 dib" tooltip={createdBy.name} />,
  },
];

interface FilterInput {
  value: string;
  options?: Array<{ id: ID; name: string }>;
}

export const formatFilterOptions = (filters: Array<FilterInput>): Array<FilterOption> =>
  filters
    .filter(({ value }) => !unFilteredCols.includes(value))
    .map(({ value, options }) => {
      return {
        value,
        name: observationFiltersToTitle[value as keyof typeof observationFiltersToTitle],
        operations:
          value === 'observed_at'
            ? [
                {
                  name: filterType.between,
                  value: filterType.between,
                },
              ]
            : [
                {
                  name: filterType.eq,
                  value: filterType.eq,
                  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                  options: options || [],
                },
                {
                  name: filterType.ne,
                  value: filterType.ne,
                  // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
                  options: options || [],
                },
              ],
      };
    });
